import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@low6-pickem/ui';

/**
 * Shared chrome for the Squares modals — the full-screen overlay, dimmed backdrop,
 * floating "Close" affordance and the dark radial-gradient card — plus the title and
 * button treatments that go inside it.
 *
 * Squares has no Dialog primitive, so each modal used to hand-roll this markup; the
 * gradient and the Saira/#53D337 button styles were copied per file. Everything that
 * is purely presentational lives here so a restyle is a single edit.
 */

/** The dark radial-gradient card background every Squares modal sits on. */
export const SQUARES_MODAL_CARD_GRADIENT =
  'radial-gradient(79.37% 53.08% at 53.27% 57.64%, #2A2A2A 35%, #121212 100%)';

/** Full-screen centring overlay a Squares modal is mounted into. */
export const SQUARES_MODAL_OVERLAY_CLASS =
  'fixed inset-0 z-50 flex items-center justify-center p-4';

/** The card's positioned wrapper — owns the shared modal width. */
export const SQUARES_MODAL_CONTAINER_CLASS = 'relative max-w-[340px] w-full';

/** Applied alongside the container when a modal's content needs to scroll. */
export const SQUARES_MODAL_SCROLL_CONTAINER_CLASS = 'max-h-[90vh] flex flex-col';

const SAIRA_BUTTON_STYLE = {
  fontFamily: '"Saira Extra Condensed", sans-serif',
  fontWeight: 800,
  fontSize: '16px',
  letterSpacing: '0.5px',
} as const;

/** Dimmed, click-to-dismiss backdrop. */
export function SquaresModalBackdrop({
  onClose,
  testId,
}: {
  onClose: () => void;
  testId?: string;
}) {
  return (
    <div
      data-test-id={testId}
      className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    />
  );
}

interface SquaresModalCloseButtonProps {
  onClose: () => void;
  disabled?: boolean;
  /**
   * Extra classes for the floating wrapper, merged over the defaults — modals that
   * render artwork overlapping the card need a higher z-index, for example.
   */
  wrapperClassName?: string;
}

/** Floating "✕ Close" affordance above the card's top-right corner. */
export function SquaresModalCloseButton({
  onClose,
  disabled = false,
  wrapperClassName,
}: SquaresModalCloseButtonProps) {
  return (
    <div className={cn('absolute -top-8 right-0 flex items-center gap-1.5 z-20', wrapperClassName)}>
      <X className="w-4 h-4 text-white" strokeWidth={2.5} />
      <button
        type="button"
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }}
        disabled={disabled}
        className="text-white hover:opacity-70 transition-opacity disabled:opacity-50 cursor-pointer"
        style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px' }}
        aria-label="Close"
      >
        Close
      </button>
    </div>
  );
}

export interface SquaresModalShellProps {
  /** Is the modal open — renders nothing when false */
  isOpen: boolean;
  /** Close handler, wired to both the backdrop and the floating Close button */
  onClose: () => void;
  /**
   * data-test-id for the overlay, and the prefix for the shell's derived hooks:
   * `${testId}-backdrop`, `${testId}-container` and `${testId}-card`.
   */
  testId: string;
  /** Disables the Close button (e.g. while a request is in flight) */
  closeDisabled?: boolean;
  /** Cap the card at the viewport height and scroll its overflow — for long content */
  scrollable?: boolean;
  /**
   * Extra classes merged over the card's defaults. Content that supplies its own
   * section padding passes the padding-clearing classes here rather than sitting on
   * the shell's `space-y` rhythm; tailwind-merge resolves the conflicts.
   */
  cardClassName?: string;
  children: ReactNode;
}

export default function SquaresModalShell({
  isOpen,
  onClose,
  testId,
  closeDisabled = false,
  scrollable = false,
  cardClassName,
  children,
}: SquaresModalShellProps) {
  if (!isOpen) return null;

  return (
    <div data-test-id={testId} className={SQUARES_MODAL_OVERLAY_CLASS}>
      <SquaresModalBackdrop onClose={onClose} testId={`${testId}-backdrop`} />

      <div
        data-test-id={`${testId}-container`}
        className={cn(
          SQUARES_MODAL_CONTAINER_CLASS,
          scrollable && SQUARES_MODAL_SCROLL_CONTAINER_CLASS
        )}
      >
        <SquaresModalCloseButton onClose={onClose} disabled={closeDisabled} />

        <div
          data-test-id={`${testId}-card`}
          className={cn(
            'rounded-xl shadow-2xl px-5 py-6 text-center space-y-5',
            scrollable && 'overflow-y-auto',
            cardClassName
          )}
          style={{ background: SQUARES_MODAL_CARD_GRADIENT }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function SquaresModalTitle({ children }: { children: ReactNode }) {
  return (
    <h2
      className="text-white uppercase"
      style={{
        fontFamily: '"Saira Extra Condensed", sans-serif',
        fontWeight: 800,
        fontSize: '24px',
        lineHeight: '26px',
        letterSpacing: '1px',
      }}
    >
      {children}
    </h2>
  );
}

interface ModalButtonProps {
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  children: ReactNode;
}

/** Solid green CTA — the primary action in a Squares modal. */
export function SquaresPrimaryButton({
  onClick,
  disabled = false,
  testId,
  children,
}: ModalButtonProps) {
  return (
    <button
      data-test-id={testId}
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-lg py-3 px-6 text-black transition-all hover:brightness-110 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ backgroundColor: '#53D337', ...SAIRA_BUTTON_STYLE }}
    >
      {children}
    </button>
  );
}

/** Green-outlined CTA — the secondary/dismissive action in a Squares modal. */
export function SquaresOutlineButton({
  onClick,
  disabled = false,
  testId,
  children,
}: ModalButtonProps) {
  return (
    <button
      data-test-id={testId}
      onClick={onClick}
      disabled={disabled}
      className="w-full rounded-lg py-3 px-6 border-2 border-[#53D337] text-[#53D337] bg-transparent transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
      style={SAIRA_BUTTON_STYLE}
    >
      {children}
    </button>
  );
}
